import { useEffect, useRef, useState } from 'react';

/**
 * The open modals, innermost last. Esc closes only the top one: the Widget
 * Playground stacks them (a picker, then "Change the data source?" over it),
 * and one key press must not unwind the whole stack.
 */
const openModals = [];

/** Focus right now — `null` where there is no DOM (tests in `node`, a server render). */
const currentFocus = () => (typeof document === 'undefined' ? null : document.activeElement);

/**
 * Esc closes the modal and focus returns to the control that opened it — the
 * DS-3 behaviour `ModalLayout` does not provide on its own (CT-5295).
 *
 * `onClose` must be the modal's **cancel**: Esc never saves. Every builder
 * modal already passes its cancel handler as `ModalLayout`'s `onClose`, so the
 * same function is handed here.
 *
 * @param {boolean} isOpen
 * @param {() => void} onClose
 * @param {{ disabled?: boolean }} [options] `disabled` while the modal is busy
 *   (saving, deleting) — the same rule as `ModalLayout`'s `closeDisabled`.
 */
export const useModalEscape = (isOpen, onClose, { disabled = false } = {}) => {
  // The control that opened the modal, read during the render that opens it:
  // the DOM is not committed yet, so focus is still on that control. Read any
  // later — in an effect — and the modal has already focused its own first
  // field. Adjusted during render, the way React recommends for state that
  // follows a changing prop.
  const [opener, setOpener] = useState(() => (isOpen ? currentFocus() : null));
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) setOpener(currentFocus());
  }

  // Read at key-press time, so a new `onClose` or `disabled` every render does
  // not re-register the listener and reorder the stack.
  const latest = useRef({ onClose, disabled });
  useEffect(() => {
    latest.current = { onClose, disabled };
  }, [onClose, disabled]);

  useEffect(() => {
    if (!isOpen) return undefined;

    const token = {};
    openModals.push(token);

    const handleKeyDown = (event) => {
      if (event.key !== 'Escape' || openModals[openModals.length - 1] !== token) return;
      if (latest.current.disabled) return;
      event.preventDefault();
      event.stopPropagation();
      latest.current.onClose?.();
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      const index = openModals.indexOf(token);
      if (index !== -1) openModals.splice(index, 1);
      // Back to the control that opened it, if it is still on the page.
      if (opener instanceof HTMLElement && opener.isConnected) opener.focus();
    };
  }, [isOpen, opener]);
};

export default useModalEscape;
